import React from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  makeStyles,
  Paper,
  Box,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import DialogTemplate from 'components/shared/DialogTemplate';
import { Input, Checkbox } from 'components/FormInputs';
import { validatePatched } from 'logic/cottage';
import { useFormik } from 'components/shared/Formik';
import amenitiesList from 'data/amenities.json';

const LANGS = {
  sv: (
    <FormattedMessage
      id="SwedishDescription"
      defaultMessage="Swedish description (optional)"
    />
  ),
  en: (
    <FormattedMessage
      id="EnglishDescription"
      defaultMessage="English description (optional)"
    />
  ),
  de: (
    <FormattedMessage
      id="GermanDescription"
      defaultMessage="German description (optional)"
    />
  ),
};

const styles = makeStyles((theme) => ({
  paperChecked: {
    paddingBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  checkbox: {
    marginLeft: theme.spacing(2),
  },
  field: {
    margin: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    width: `calc(100% - ${theme.spacing(4)}px)`,
  },
}));

const EditAmenities = ({
  isLoading,
  onSubmit,
  onClose,
  cottage,
}) => {
  const classes = styles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const intl = useIntl();
  const amenities = (cottage.amenities || []).reduce((acc, cur) => {
    return {
      ...acc,
      [cur.key]: {
        checked: true,
        comment_en: cur.comment_en,
        comment_sv: cur.comment_sv,
        comment_de: cur.comment_de,
      },
    };
  }, {});
  const formik = useFormik({
    initialValues: {
      amenities,
    },
    enableReinitialize: true,
    validate: validatePatched({ intl, dialog: 'amenities' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const {
    values, errors, handleSubmit, handleChange, submitForm,
    handleBlur,
  } = formik;

  const dirty = Object.keys(amenities).length !== Object.keys(values.amenities).filter((key) => values.amenities[key].checked).length ||
    Object.keys(amenities).some((key) => {
      if (!values.amenities[key]) {
        return true;
      }

      if (values.amenities[key].checked !== amenities[key].checked) {
        return true;
      }

      const check = (
        amenities[key].comment_de !== (values.amenities[key].comment_de || null) ||
        amenities[key].comment_sv !== (values.amenities[key].comment_sv || null) ||
        amenities[key].comment_en !== (values.amenities[key].comment_en || null)
      );

      return check && values.amenities[key].checked;
    });

  return (
    <DialogTemplate
      title={(
        <>
          <FormattedMessage
            id="Amenities"
            defaultMessage="Amenities"
          />
          &nbsp;
          <>
            #{cottage?.id}
            {
              !isSmall && (
                <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>
              )
            }
          </>
        </>
      )}
      onSave={submitForm}
      pristine={!dirty}
      onClose={onClose}
      isUpdating={isLoading}
    >
      <Box pb={12}>
        <form onSubmit={handleSubmit} noValidate>
          {amenitiesList.map((amenity) => {
            const checked = values.amenities && values.amenities[amenity.key] && values.amenities[amenity.key].checked;

            return (
              <Paper key={amenity.key} className={classNames(checked && classes.paperChecked)}>
                <Checkbox
                  name={`amenities.${amenity.key}.checked`}
                  onChange={handleChange}
                  value={values.amenities[amenity.key] ? values.amenities[amenity.key].checked : false}
                  label={intl.formatMessage({
                    id: `amenities.${amenity.key}`,
                    defaultMessage: amenity.name,
                  })}
                  className={classes.checkbox}
                />
                {checked && <Divider />}
                {checked && Object.keys(LANGS).map((lang) => (
                  <Input
                    key={lang}
                    name={`amenities.${amenity.key}.comment_${lang}`}
                    value={values.amenities[amenity.key][`comment_${lang}`]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    label={LANGS[lang]}
                    className={classes.field}
                  />
                ))}
              </Paper>
            );
          })}
        </form>
      </Box>
    </DialogTemplate>
  );
};

EditAmenities.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    amenities: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

export default EditAmenities;
